import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '@lib/api/client';
import { getPortalInvoicePaymentLink } from '@lib/api/portal';
import { generateInvoicePdf } from '@/lib/utils/generateInvoicePdf';

// ── Client invoice detail (portal audit) ────────────────────────────────────
// The list's "PDF" button used to hit a nonexistent endpoint and open raw
// JSON. Now: a proper branded invoice view with Pay, Save PDF and Print.

interface InvItem { description: string; item_type: string; quantity: number; unit: string | null; unit_price: number; total: number }
interface InvDetail {
  id: number; invoice_number: string; status: string;
  invoice_date: string | null; due_date: string | null;
  subtotal: number; tax_rate: number; tax_amount: number;
  gst_amount: number; pst_amount: number;
  total: number; amount_paid: number; balance_due: number;
  notes: string | null; job_title: string | null; site_address: string | null;
  client_name: string | null; client_company: string | null; client_address: string | null;
  items: InvItem[];
  company: { name: string; address: string; phone: string; email: string; tagline: string };
}

const fmt = (n: unknown) => `$${(Number(n) || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string | null) => d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

export const ClientInvoiceDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [inv, setInv] = useState<InvDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  const [saving, setSaving] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    apiClient.get(`/portal/me/invoices/${id}`)
      .then(r => setInv(r.data.data))
      .catch(() => setError('We couldn\'t find that invoice.'))
      .finally(() => setLoading(false));
  }, [id]);

  const pay = async () => {
    if (!inv) return;
    setPaying(true);
    setPayError('');
    try {
      const { url } = await getPortalInvoicePaymentLink(inv.id);
      window.location.href = url;
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setPayError(ax.response?.data?.error || 'Online payment isn\'t available right now — you can pay by e-Transfer to billing@ochrepoint.ca.');
      setPaying(false);
    }
  };

  const savePdf = async () => {
    if (!printRef.current || !inv) return;
    setSaving(true);
    try { await generateInvoicePdf(printRef.current, `Invoice-${inv.invoice_number}.pdf`); }
    catch { setPayError('Could not build the PDF — use Print and choose "Save as PDF" instead.'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="space-y-3 pt-2"><div className="h-40 rounded-xl bg-gray-100 animate-pulse" /><div className="h-64 rounded-xl bg-gray-100 animate-pulse" /></div>;
  if (error || !inv) {
    return (
      <div className="pt-8 text-center space-y-3">
        <span className="material-symbols-outlined text-gray-300 text-4xl">receipt_long</span>
        <p className="text-sm text-gray-500">{error || 'Invoice not found.'}</p>
        <button onClick={() => navigate('/portal/client/invoices')} className="text-sm font-semibold text-[#C77D3F]">← Back to invoices</button>
      </div>
    );
  }

  const balance = Number(inv.balance_due);

  return (
    <div className="space-y-4 pb-6">
      {/* Toolbar (hidden in print) */}
      <div className="flex items-center gap-2 pt-1 print:hidden">
        <button onClick={() => navigate('/portal/client/invoices')}
          className="flex items-center gap-1 text-sm font-semibold text-[#C77D3F]">
          <span className="material-symbols-outlined text-base">arrow_back_ios</span> Invoices
        </button>
        <div className="ml-auto flex gap-2">
          <button onClick={savePdf} disabled={saving}
            className="flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-xl border border-gray-300 text-gray-600 active:scale-95 disabled:opacity-50">
            <span className="material-symbols-outlined text-base">download</span>
            {saving ? 'Building…' : 'Save PDF'}
          </button>
          <button onClick={() => window.print()}
            className="flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-xl border border-gray-300 text-gray-600 active:scale-95">
            <span className="material-symbols-outlined text-base">print</span> Print
          </button>
        </div>
      </div>

      {payError && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 print:hidden">{payError}</p>}

      {/* Printable invoice */}
      <div ref={printRef} className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-7">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xl font-black text-[#2C1810]">{inv.company.name}</p>
            <p className="text-xs text-gray-500 italic">{inv.company.tagline}</p>
            <p className="text-xs text-gray-500 mt-1">{inv.company.address}<br />{inv.company.phone} · {inv.company.email}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-bold uppercase tracking-widest text-[#C77D3F]">Invoice</p>
            <p className="text-sm font-bold text-gray-900">{inv.invoice_number}</p>
            <p className="text-xs text-gray-500 mt-1">Issued {fmtDate(inv.invoice_date)}</p>
            {inv.due_date && <p className="text-xs text-gray-500">Due {fmtDate(inv.due_date)}</p>}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Billed to</p>
            <p className="font-semibold text-gray-900">{inv.client_name}</p>
            {inv.client_company && <p className="text-gray-600 text-xs">{inv.client_company}</p>}
            {inv.client_address && <p className="text-gray-500 text-xs">{inv.client_address}</p>}
          </div>
          {inv.job_title && (
            <div className="text-right">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Project</p>
              <p className="font-semibold text-gray-900">{inv.job_title}</p>
              {inv.site_address && <p className="text-gray-500 text-xs">{inv.site_address}</p>}
            </div>
          )}
        </div>

        <table className="w-full text-sm mt-5">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-200">
              <th className="py-2 font-semibold">Description</th>
              <th className="py-2 font-semibold text-right">Qty</th>
              <th className="py-2 font-semibold text-right">Price</th>
              <th className="py-2 font-semibold text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {inv.items.map((it, i) => it.item_type === 'heading' ? (
              <tr key={i} className="bg-gray-50/60">
                <td colSpan={4} className="py-1.5 text-xs font-bold uppercase tracking-wide text-gray-500">{it.description}</td>
              </tr>
            ) : (
              <tr key={i}>
                <td className="py-2 pr-3 text-gray-800">{it.description}</td>
                <td className="py-2 px-1.5 text-right text-gray-600 whitespace-nowrap">{Number(it.quantity)}{it.unit ? ` ${it.unit}` : ''}</td>
                <td className="py-2 px-1.5 text-right text-gray-600 whitespace-nowrap">{fmt(it.unit_price)}</td>
                <td className="py-2 pl-1.5 text-right font-semibold text-gray-900 whitespace-nowrap">{fmt(it.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 ml-auto max-w-[240px] space-y-1 text-sm">
          <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{fmt(inv.subtotal)}</span></div>
          {Number(inv.tax_amount) > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>{Number(inv.gst_amount) > 0 && Number(inv.pst_amount) > 0 ? 'GST + PST' : 'GST'} ({Number(inv.tax_rate)}%)</span>
              <span>{fmt(inv.tax_amount)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-gray-900 border-t border-gray-200 pt-1"><span>Total</span><span>{fmt(inv.total)}</span></div>
          {Number(inv.amount_paid) > 0 && (
            <div className="flex justify-between text-green-700"><span>Paid</span><span>−{fmt(inv.amount_paid)}</span></div>
          )}
          <div className={`flex justify-between font-black ${balance > 0 ? 'text-[#C77D3F]' : 'text-green-700'}`}>
            <span>Balance due</span><span>{fmt(balance)}</span>
          </div>
        </div>

        {inv.notes && <p className="mt-4 text-xs text-gray-500 border-t border-gray-100 pt-3">{inv.notes}</p>}
        <p className="mt-4 text-[10px] text-gray-400">
          Pay online from your portal, or by Interac e-Transfer to billing@ochrepoint.ca (reference {inv.invoice_number}). Thank you for your business.
        </p>
      </div>

      {balance > 0 && !['cancelled', 'revised'].includes(inv.status) && (
        <button onClick={pay} disabled={paying}
          className="w-full py-3.5 bg-[#C77D3F] text-white rounded-2xl text-sm font-bold active:scale-[0.99] disabled:opacity-60 print:hidden">
          {paying ? 'Opening secure checkout…' : `Pay ${fmt(balance)} now`}
        </button>
      )}
    </div>
  );
};
